import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import {
  Box,
  type BoxProps,
  Text,
  type TextProps,
} from '@frontend/legacy-components';

import { PERMISSIONS, RELAY_URL, SUPPLIER_ROLES } from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useFeatureFlagValue } from 'store/session/hooks';
import { getGroupMemberSupplierRoles } from 'store/session/selectors';

import { partLibraryConfig, buildQuotingToolConfig } from './navConfigs';

interface TabButtonProps {
  title: ReactNode;
  icon: ReactNode;
  iconTextProps: TextProps;
  boxProps?: BoxProps;
}

const TabButton = ({
  title,
  icon,
  iconTextProps,
  boxProps,
}: TabButtonProps) => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    height="100%"
    margin="auto 0"
    {...boxProps}
  >
    <Box m="0 auto">{icon}</Box>
    <Text
      m="0 auto"
      mb={-0.25}
      color="currentColor"
      casing="uppercase"
      fontWeight="heavy"
      {...iconTextProps}
    >
      {title}
    </Text>
  </Box>
);

export interface NavItemsProps {
  iconTextProps: TextProps;
  isMobile: boolean;
}

const NavItems = ({ iconTextProps, isMobile }: NavItemsProps) => {
  const { pathname: path } = useLocation();
  const isQuotingToolAvailable = useFeatureFlagValue(
    featureFlagKeys.isQuotingToolAvailable,
  );
  const supplierRoles: Record<string, string> = useSelector(
    getGroupMemberSupplierRoles,
  );

  const config = isQuotingToolAvailable
    ? buildQuotingToolConfig(RELAY_URL)
    : partLibraryConfig;

  const iconActiveProps = isMobile
    ? {
        color: 'brand.secondary',
      }
    : {
        bg: 'brand.secondary',
        color: 'feature.onInteraction',
        _hover: { color: 'feature.onInteraction' },
        _active: { color: 'feature.onInteraction' },
        _focus: { color: 'feature.onInteraction' },
      };

  const iconInactiveProps = isMobile
    ? { color: 'feature.secondary' }
    : {
        transition: 'all 0.2s ease',
        color: 'feature.onInteraction',
        _hover: { bg: 'transparent', color: 'brand.secondary' },
        _active: { bg: 'transparent', color: 'brand.secondary' },
        _focus: { bg: 'transparent', color: 'brand.secondary', border: 'none' },
        _focusVisible: { outline: 'none !important' },
      };

  const iconDisabledProps = {
    color: 'feature.onInteraction',
    cursor: 'not-allowed',
    opacity: '50%',
    tabIndex: -1,
  };

  return config.map((navItem) => {
    const isExternal = navItem.external;
    let isDisabled =
      supplierRoles[navItem.supplierRoleName ?? ''] === PERMISSIONS.NO_ACCESS;
    let url = navItem.url;
    const mediaBreakpoint = navItem.mediaBreakpoint;
    if (
      navItem.supplierRoleName === SUPPLIER_ROLES.PROCESSES &&
      supplierRoles[navItem.supplierRoleName] === PERMISSIONS.NO_ACCESS
    ) {
      isDisabled = false;
      url = '/processes/materials';
    }
    return (
      <Box
        as={isDisabled ? 'div' : isExternal ? 'a' : NavLink}
        data-testid={navItem.title}
        display={
          mediaBreakpoint === 'base'
            ? {
                base: 'unset',
              }
            : {
                base: 'none',
                [mediaBreakpoint]: 'unset',
              }
        }
        height="100%"
        key={navItem.key}
        width={isMobile ? '100%' : 10}
        sx={{ '&.active': iconActiveProps }}
        {...(isDisabled ? iconDisabledProps : iconInactiveProps)}
        {...(isDisabled ? {} : isExternal ? { href: url } : { to: url })}
        isActive={() => {
          const urls = [url, ...(navItem.altActiveUrls || [])];
          return urls.some(
            (url) => url.startsWith(path) || path.startsWith(url),
          );
        }}
      >
        <TabButton
          icon={navItem.icon}
          key={navItem.key}
          title={navItem.title}
          iconTextProps={iconTextProps}
        />
      </Box>
    );
  });
};

export default NavItems;
